import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import { Mail, Lock, Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { loginAdmin } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import logo from "@/assets/logo.png";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onTouched" });

  const onSubmit = async (data) => {
    setServerError("");
    setLoading(true);
    try {
      await loginAdmin(data.email, data.password);
      navigate("/AdminDashboard");
    } catch (err) {
      setServerError(err.message || t("loginFailed"));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <style>{`
        .login-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .glow-effect {
          box-shadow: 0 0 60px rgba(59, 130, 246, 0.3);
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl shadow-lg shadow-blue-500/30 mb-4 glow-effect"
          >
            <img src={logo} alt="Logo" className="h-full w-full object-contain" />
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-2">Luxkgate</h1>
          <p className="text-slate-400">{t("paymentGatewayAdminPanel")}</p>
        </div>

        {/* Login Card */}
        <Card className="login-card border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-white">{t("welcomeBackAdmin")}</CardTitle>
            <CardDescription className="text-slate-400">
              {t("signInToAccessAdminDashboard")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {/* Server Error */}
              {serverError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {serverError}
                </motion.div>
              )}
 
              {/* Email Field */}
              <div className="space-y-1">
                <Label className="text-slate-300">{t("emailAddress")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="email"
                    placeholder="admin@luxkgate.com"
                    className={`pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 ${
                      errors.email ? "border-red-500/60 focus:border-red-500" : ""
                    }`}
                    {...register("email", {
                      required: "Email address is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                  />
                </div>
                {errors.email && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-1 text-xs text-red-400 mt-1"
                  >
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    {errors.email.message}
                  </motion.p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1">
                <Label className="text-slate-300">{t("password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className={`pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 ${
                      errors.password ? "border-red-500/60 focus:border-red-500" : ""
                    }`}
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters",
                      },
                      validate: {
                        hasUppercase: (value) => /[A-Z]/.test(value) || "Password must contain at least one uppercase letter",
                        hasLowercase: (value) => /[a-z]/.test(value) || "Password must contain at least one lowercase letter",
                        hasSpecialChar: (value) => /[!@#$%^&*(),.?":{}|<>]/.test(value) || "Password must contain at least one special character",
                      }
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-1 text-xs text-red-400 mt-1"
                  >
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    {errors.password.message}
                  </motion.p>
                )}
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-400 cursor-pointer">
                  <input type="checkbox" className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500/20" />
                  {t("rememberMe")}
                </label>
                <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">
                  {t("forgotPassword")}
                </a>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-5 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("signIn")
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700/50 text-center space-y-2">
              <p className="text-slate-500 text-sm">
                {t("accessRestrictedToAuthorizedOnly")}
              </p>
              <a href="/staff-login" className="text-xs text-blue-400 hover:text-blue-300 transition-colors inline-block">
                {t("staffMemberClickHereToLogin")}
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} Luxkgate. {t("allRightsReserved")}
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <a href="#" className="text-slate-500 hover:text-slate-400 text-xs transition-colors">{t("privacyPolicy")}</a>
            <span className="text-slate-700">•</span>
            <a href="#" className="text-slate-500 hover:text-slate-400 text-xs transition-colors">{t("termsOfService")}</a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
